import { useStatsigClient } from "@statsig/react-bindings";
import { useMemo } from "react";

import { useEnableLineChannelCreation } from "@/features/line/useEnableLineChannelCreation";
import { interlude } from "@/interlude";

/**
 * Every channel the organization has, as the Channels page lists them: LINE
 * channels come from their own endpoint for orgs in the channel migration or
 * the LINE creation gate, and everything else from the channels endpoint.
 *
 * `isSuccess` is true only when every list that applies has loaded, so a count
 * built from `channels` is never a partial one. `isPending` follows the main
 * channels list, which is what the Channels table waits for.
 */
export const useOrgChannels = () => {
  const statsigClient = useStatsigClient();
  const enableLineChannelCreation = useEnableLineChannelCreation();
  const channelsQuery = interlude.channel.useChannels();
  const includeLineChannels =
    statsigClient.checkGate("channel_migration") || enableLineChannelCreation;
  // Orgs that create LINE through the gate must see the channel they made,
  // even when they are outside the channel migration.
  const lineChannelsQuery = interlude.line.useChannels(undefined, {
    enabled: includeLineChannels,
  });

  const channels = useMemo(
    () => [
      ...(lineChannelsQuery.isSuccess ? lineChannelsQuery.data.channels : []),
      ...(channelsQuery.isSuccess ? channelsQuery.data.channels : []),
    ],
    [
      channelsQuery.data?.channels,
      channelsQuery.isSuccess,
      lineChannelsQuery.data?.channels,
      lineChannelsQuery.isSuccess,
    ],
  );
  const isSuccess =
    channelsQuery.isSuccess &&
    (!includeLineChannels || lineChannelsQuery.isSuccess);

  const isPending = channelsQuery.isPending;

  return useMemo(
    () => ({ channels, isSuccess, isPending }),
    [channels, isSuccess, isPending],
  );
};
